import { useState, useEffect, useCallback } from 'react';
import { customersService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { writeClientAudit } from '../lib/clientAudit';
import { customerAuditSummary } from '../lib/moduleAudit';
import { initialCustomers } from '../data/customers';
import type { Customer } from '../types';

interface UseCustomersReturn {
  customers: Customer[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (customer: Omit<Customer, 'id'>) => Promise<Customer>;
  update: (id: number, updates: Partial<Customer>) => Promise<Customer>;
  remove: (id: number) => Promise<void>;
  getById: (id: number) => Customer | undefined;
}

export const useCustomers = (enabled = true): UseCustomersReturn => {
  const [customers, setCustomers] = useState<Customer[]>(initialCustomers);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCustomers = useCallback(async () => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      setCustomers(initialCustomers);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await customersService.getAll();
      setCustomers(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load customers';
      setError(errorMessage);
      console.error('Error fetching customers:', err);
      setCustomers(initialCustomers);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchCustomers();
  }, [fetchCustomers, enabled]);

  const create = useCallback(async (customer: Omit<Customer, 'id'>): Promise<Customer> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const newCustomer: Customer = {
        ...customer,
        id: Math.max(...customers.map(c => c.id), 0) + 1,
      };
      setCustomers(prev => [...prev, newCustomer]);
      return newCustomer;
    }

    setError(null);
    try {
      const newCustomer = await customersService.create(customer);
      setCustomers(prev => [...prev, newCustomer]);
      writeClientAudit({
        action: 'customer.added',
        entityType: 'customer',
        entityId: String(newCustomer.id),
        summary: customerAuditSummary('added', newCustomer.name),
      });
      return newCustomer;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create customer';
      setError(errorMessage);
      throw err;
    }
  }, [customers]);

  const update = useCallback(async (id: number, updates: Partial<Customer>): Promise<Customer> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const updatedCustomer = customers.find(c => c.id === id);
      if (!updatedCustomer) throw new Error('Customer not found');
      const newCustomer = { ...updatedCustomer, ...updates };
      setCustomers(prev => prev.map(c => c.id === id ? newCustomer : c));
      return newCustomer;
    }

    setError(null);
    try {
      const previous = customers.find(c => c.id === id);
      const updatedCustomer = await customersService.update(id, updates);
      setCustomers(prev => prev.map(c => c.id === id ? updatedCustomer : c));
      const changedKeys = Object.keys(updates).filter((key) => updates[key as keyof typeof updates] !== undefined);
      const pointsOnly = changedKeys.every((key) => key === 'loyaltyPoints' || key === 'carryoverRequestCount60d' || key === 'lastCarryoverAt');
      if (!pointsOnly) {
        writeClientAudit({
          action: 'customer.updated',
          entityType: 'customer',
          entityId: String(id),
          summary: customerAuditSummary('saved', updatedCustomer.name || previous?.name || 'Customer', previous as unknown as Record<string, unknown>, updates as Record<string, unknown>),
        });
      }
      return updatedCustomer;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update customer';
      setError(errorMessage);
      throw err;
    }
  }, [customers]);

  const remove = useCallback(async (id: number): Promise<void> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      setCustomers(prev => prev.filter(c => c.id !== id));
      return;
    }

    setError(null);
    try {
      const previous = customers.find(c => c.id === id);
      await customersService.delete(id);
      setCustomers(prev => prev.filter(c => c.id !== id));
      writeClientAudit({
        action: 'customer.removed',
        entityType: 'customer',
        entityId: String(id),
        summary: customerAuditSummary('removed', previous?.name || 'Customer'),
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete customer';
      setError(errorMessage);
      throw err;
    }
  }, [customers]);

  const getById = useCallback((id: number): Customer | undefined => {
    return customers.find(c => c.id === id);
  }, [customers]);

  return {
    customers,
    loading,
    error,
    refresh: fetchCustomers,
    create,
    update,
    remove,
    getById,
  };
};

